import { useLanguage } from "@/contexts/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Calendar, Info, Edit3, Plus, AlertCircle, RefreshCw, Check } from "lucide-react";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { ConfidentialityDisclaimer } from "@/components/footprints/ConfidentialityDisclaimer";
import { Button } from "@/components/ui/button";

interface YearSelectionStepProps {
  selectedYear: string;
  onYearChange: (year: string) => void;
  loading: boolean;
  isEditMode?: boolean;
  isLoadingData?: boolean;
  loadedFormData?: Record<string, any> | null;
  company?: Record<string, any> | null;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
  hasUnsavedChanges?: boolean;
}

// Constants
const CURRENT_YEAR = new Date().getFullYear();
const START_YEAR = 2023;

const getStatusBadge = (status: "draft" | "submitted" | null, t: any) => {
  switch (status) {
    case "draft":
      return (
        <Badge variant="secondary" className="text-xs">
          {t("footprints.consumption.draft")}
        </Badge>
      );
    case "submitted":
      return (
        <Badge variant="default" className="text-xs bg-teal-600 hover:bg-teal-700 text-white">
          {t("footprints.consumption.complete")}
        </Badge>
      );
    default:
      return null;
  }
};

const convertFirestoreTimestamp = (timestamp: any): Date | null => {
  if (!timestamp) return null;

  // Handle Firestore timestamp (has .seconds property)
  if (timestamp && typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date(timestamp.seconds * 1000);
  }

  // Handle regular Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }

  // Try to parse as date string
  if (typeof timestamp === "string") {
    return new Date(timestamp);
  }

  return null;
};

export const YearSelectionStep = ({
  selectedYear,
  onYearChange,
  loading,
  isEditMode,
  isLoadingData,
  loadedFormData,
  company,
  onSaveDraft,
  isSavingDraft,
  hasUnsavedChanges,
}: YearSelectionStepProps) => {
  const { t } = useLanguage();

  // Generate available years (from 2023 to previous year, not including current year)
  const availableYears = useMemo(() => {
    const previousYear = CURRENT_YEAR - 1;
    return Array.from({ length: previousYear - START_YEAR + 1 }, (_, i) => (START_YEAR + i).toString()).reverse(); // Most recent first
  }, []);

  // Calculate consumption summary from loaded form data
  const consumptionSummary = useMemo(() => {
    if (!loadedFormData) return null;

    const activityType = loadedFormData.generalData?.activityType;
    const isMixedCompany = activityType === "extraction_and_transformation";

    let totalCarbonFootprint = 0;
    let totalHydricFootprint = 0;

    if (isMixedCompany) {
      const materials = {
        extraction: loadedFormData.annualConsumptions?.extraction?.materials,
        transformation: loadedFormData.annualConsumptions?.transformation?.materials
      };
      const materialsCarbon = (materials.extraction?.total_pc_mat || 0) + (materials.transformation?.total_pc_mat || 0);
      const materialsHydric = (materials.extraction?.total_ph_mat || 0) + (materials.transformation?.total_ph_mat || 0);
      
      const electricity = {
        extraction: loadedFormData.annualConsumptions?.extraction?.electricity,
        transformation: loadedFormData.annualConsumptions?.transformation?.electricity
      };
      const electricityCarbon = (electricity.extraction?.total_pc_ele || 0) + (electricity.transformation?.total_pc_ele || 0);
      const electricityHydric = (electricity.extraction?.total_ph_ele || 0) + (electricity.transformation?.total_ph_ele || 0);
      
      const fuels = {
        extraction: loadedFormData.annualConsumptions?.extraction?.fuels,
        transformation: loadedFormData.annualConsumptions?.transformation?.fuels
      };
      const fuelsCarbon = (fuels.extraction?.total_pc_comb || 0) + (fuels.transformation?.total_pc_comb || 0);
      const fuelsHydric = (fuels.extraction?.total_ph_comb || 0) + (fuels.transformation?.total_ph_comb || 0);
      
      const residue = {
        extraction: loadedFormData.annualConsumptions?.extraction?.residue,
        transformation: loadedFormData.annualConsumptions?.transformation?.residue
      };
      const residueCarbon = (residue.extraction?.total_pc_res || 0) + (residue.transformation?.total_pc_res || 0);
      
      const water = {
        extraction: loadedFormData.annualConsumptions?.extraction?.water,
        transformation: loadedFormData.annualConsumptions?.transformation?.water
      };
      const waterHydric = (water.extraction?.total_ph_agua_azul || 0) + 
                          (water.extraction?.total_ph_agua_cinzenta || 0) + 
                          (water.transformation?.total_ph_agua_azul || 0) + 
                          (water.transformation?.total_ph_agua_cinzenta || 0);
      
      totalCarbonFootprint = materialsCarbon + electricityCarbon + fuelsCarbon + residueCarbon;
      totalHydricFootprint = materialsHydric + electricityHydric + fuelsHydric + waterHydric;
    } else {
      totalCarbonFootprint =
        (loadedFormData.materials?.total_pc_mat || 0) +
        (loadedFormData.electricity?.total_pc_ele || 0) +
        (loadedFormData.fuels?.total_pc_comb || 0) +
        (loadedFormData.residue?.total_pc_res || 0);

      totalHydricFootprint =
        (loadedFormData.materials?.total_ph_mat || 0) +
        (loadedFormData.electricity?.total_ph_ele || 0) +
        (loadedFormData.fuels?.total_ph_comb || 0) +
        (loadedFormData.water?.total_ph_agua || 0);
    }
    return {
      materialsCount: isMixedCompany 
        ? (loadedFormData.annualConsumptions?.extraction?.materials?.entries?.length || 0) + 
          (loadedFormData.annualConsumptions?.transformation?.materials?.entries?.length || 0)
        : loadedFormData.materials?.entries?.length || 0,
      electricityCount: isMixedCompany
        ? (loadedFormData.annualConsumptions?.extraction?.electricity?.entries?.length || 0) + 
          (loadedFormData.annualConsumptions?.transformation?.electricity?.entries?.length || 0)
        : loadedFormData.electricity?.entries?.length || 0,
      fuelsCount: isMixedCompany
        ? (loadedFormData.annualConsumptions?.extraction?.fuels?.entries?.length || 0) + 
          (loadedFormData.annualConsumptions?.transformation?.fuels?.entries?.length || 0)
        : loadedFormData.fuels?.entries?.length || 0,
      residueCount: isMixedCompany
        ? (loadedFormData.annualConsumptions?.extraction?.residue?.entries?.length || 0) + 
          (loadedFormData.annualConsumptions?.transformation?.residue?.entries?.length || 0)
        : loadedFormData.residue?.entries?.length || 0,
      waterCount: isMixedCompany
        ? (loadedFormData.annualConsumptions?.extraction?.water?.waterEntries?.length || 0) + 
          (loadedFormData.annualConsumptions?.transformation?.water?.waterEntries?.length || 0)
        : loadedFormData.water?.waterEntries?.length || 0,
      totalCarbonFootprint,
      totalHydricFootprint,
    };
  }, [loadedFormData]);

  // Calculate product count from loaded form data
  const productCount = useMemo(() => {
    if (!loadedFormData || !loadedFormData.products) return 0;
    
    // Use company activityType as source of truth, fallback to formData
    const activityType = company?.activityType || loadedFormData.generalData?.activityType;
    const products = loadedFormData.products.entries || [];
    
    let filteredCount = 0;
    
    if (activityType === "extraction") {
      // For extraction companies, only count extraction products
      filteredCount = products.filter((p: any) => !p.productFlow || p.productFlow === "extraction").length;
    } else if (activityType === "transformation") {
      // For transformation companies, only count transformation products
      filteredCount = products.filter((p: any) => !p.productFlow || p.productFlow === "transformation").length;
    } else if (activityType === "extraction_and_transformation") {
      // For mixed companies, count all products
      filteredCount = products.length;
    } else {
      // Fallback - count all products
      filteredCount = products.length;
    }
  
    return filteredCount;
  }, [loadedFormData]);

  // Determine if we should show existing data
  const shouldShowExistingData = useMemo(() => {
    return isEditMode && loadedFormData && Object.keys(loadedFormData).length > 0;
  }, [isEditMode, loadedFormData]);

  // Get status and last updated from metadata
  const dataStatus = useMemo(() => {
    return shouldShowExistingData ? loadedFormData?._metadata?.originalStatus || "draft" : null;
  }, [shouldShowExistingData, loadedFormData]);

  const dataLastUpdated = useMemo(() => {
    if (!shouldShowExistingData) return null;
    const lastSavedData = loadedFormData?._metadata?.lastSaved;
    return convertFirestoreTimestamp(lastSavedData);
  }, [shouldShowExistingData, loadedFormData]);

  const hasGeneralDataContent = useMemo(() => {
    const generalData = loadedFormData?.generalData;
    if (!generalData) return false;

    // Use company activityType as source of truth, fallback to formData
    const activityType = company?.activityType || generalData.activityType;
    
    let hasGeneralData = false;

    if (activityType === "extraction") {
      // For extraction companies, check extraction fields + stone type
      hasGeneralData =
        generalData.stoneTypeId &&
        generalData.stoneTypeId !== "" &&
        generalData.prod_ext &&
        generalData.prod_ext > 0 &&
        generalData.fat_ext &&
        generalData.fat_ext > 0 &&
        generalData.pvp_caro_ext &&
        generalData.pvp_caro_ext > 0 &&
        generalData.pvp_barato_ext &&
        generalData.pvp_barato_ext > 0;
    } else if (activityType === "transformation") {
      // For transformation companies, check transformation fields (no stone type required)
      hasGeneralData =
        generalData.prod_trsf &&
        generalData.prod_trsf > 0 &&
        generalData.fat_trsf &&
        generalData.fat_trsf > 0 &&
        generalData.pvp_caro_trsf &&
        generalData.pvp_caro_trsf > 0 &&
        generalData.pvp_barato_trsf &&
        generalData.pvp_barato_trsf > 0 &&
        generalData.rend &&
        generalData.rend > 0;
    } else if (activityType === "extraction_and_transformation") {
      // For mixed companies, check both extraction and transformation fields
      const hasExtractionData =
        generalData.stoneTypeId &&
        generalData.stoneTypeId !== "" &&
        generalData.prod_ext &&
        generalData.prod_ext > 0 &&
        generalData.fat_ext &&
        generalData.fat_ext > 0 &&
        generalData.pvp_caro_ext &&
        generalData.pvp_caro_ext > 0 &&
        generalData.pvp_barato_ext &&
        generalData.pvp_barato_ext > 0;

      const hasTransformationData =
        generalData.prod_trsf &&
        generalData.prod_trsf > 0 &&
        generalData.fat_trsf &&
        generalData.fat_trsf > 0 &&
        generalData.pvp_caro_trsf &&
        generalData.pvp_caro_trsf > 0 &&
        generalData.pvp_barato_trsf &&
        generalData.pvp_barato_trsf > 0 &&
        generalData.rend &&
        generalData.rend > 0;

      hasGeneralData = hasExtractionData && hasTransformationData;
    }

    return hasGeneralData;
  }, [loadedFormData, company?.activityType]);

  return (
    <div className="w-full max-w-5xl mx-auto">
      <ConfidentialityDisclaimer />
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
        <div className="flex flex-col items-start md:items-center">
          {/* Calendar icon and field label */}
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-5 w-5 text-teal-600" />
            <h2 className="text-lg font-medium text-gray-700">{t("footprints.selectYear")}</h2>
          </div>

          {/* Year selection dropdown */}
          <div className="w-full max-w-md mx-auto md:mx-0">
            <Select value={selectedYear} onValueChange={onYearChange} disabled={loading}>
              <SelectTrigger className="h-12 text-base w-full border-gray-300">
                <SelectValue placeholder={t("footprints.selectYearPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year} className="text-base">
                    <div className="flex items-center justify-between w-full gap-3">
                      <span>{year}</span>
                      <div className="flex items-center gap-2">
                        {year === selectedYear ? (
                          <div className="w-2 h-2 bg-teal-600 rounded-full" />
                        ) : (
                          <Plus className="h-3 w-3 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Year status information */}
        {selectedYear && (
          <div className="mt-6">
            {isLoadingData ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
                    <RefreshCw className="h-5 w-5 animate-spin text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {t("footprints.loadingDataFor", { year: selectedYear })}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{t("footprints.verifyingExistingData")}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                {/* Status header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-teal-100 rounded-lg">
                    <Edit3 className="h-5 w-5 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {shouldShowExistingData
                        ? t("footprints.continuingCalculationFor", { year: selectedYear })
                        : t("footprints.newCalculationFor", { year: selectedYear })}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {shouldShowExistingData ? (
                        <>
                          <span className="text-sm text-gray-600">{t("footprints.existingDataFound")}</span>
                          {getStatusBadge(dataStatus, t)}
                        </>
                      ) : (
                        <p className="text-sm text-gray-600 mt-1">{t("footprints.newCalculationWillStart")}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Other sections grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Dados Gerais */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                      {t("footprints.generalData.title")}
                    </div>
                    <div className="text-lg font-semibold">
                      {hasGeneralDataContent ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-teal-600" />
                          <span className="text-teal-600">{t("footprints.filled")}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-orange-600" />
                          <span className="text-orange-600">{t("footprints.pending")}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Produtos */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                      {t("products.title")}
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {productCount > 0 ? (
                        <>
                          {productCount}{" "}
                          <span className="text-sm font-normal text-gray-600">
                            {productCount === 1
                              ? t("footprints.revisionSubmission.productSingular")
                              : t("footprints.revisionSubmission.productPlural")}
                          </span>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-orange-600" />
                          <span className="text-orange-600">{t("footprints.pending")}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pegada de Carbono */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                      {t("common.carbonFootprint")}
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {consumptionSummary?.totalCarbonFootprint.toFixed(1) || "0.0"}{" "}
                      <span className="text-sm font-normal text-gray-600">kg CO₂e</span>
                    </div>
                  </div>

                  {/* Pegada Hídrica */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                      {t("common.hydricFootprint")}
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {consumptionSummary?.totalHydricFootprint.toFixed(1) || "0.0"}{" "}
                      <span className="text-sm font-normal text-gray-600">m³</span>
                    </div>
                  </div>
                </div>

                {/* Unsaved changes card, styled and placed like in AnnualConsumptionsStep */}
                {shouldShowExistingData && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex items-center gap-2 text-sm ${
                            hasUnsavedChanges ? "text-gray-600" : "text-teal-600"
                          }`}
                        >
                          {hasUnsavedChanges ? <AlertCircle className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                          <span>
                            {hasUnsavedChanges
                              ? t("footprints.consumption.unsavedChanges")
                              : t("footprints.consumption.allSaved")}
                          </span>
                        </div>
                        {dataLastUpdated && (
                          <div className="text-xs text-gray-500">
                            {t("footprints.consumption.lastSaved")}: {dataLastUpdated.toLocaleString("pt-PT")}
                          </div>
                        )}
                      </div>
                      {hasUnsavedChanges && onSaveDraft && (
                        <Button
                          onClick={onSaveDraft}
                          disabled={isSavingDraft}
                          variant="outline"
                          size="sm"
                          className="border-teal-200 text-teal-600 hover:bg-teal-50"
                        >
                          {isSavingDraft ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              {t("footprints.consumption.saving")}
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              {t("footprints.consumption.saveDraft")}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Disclaimer message when no year is selected */}
        {!selectedYear && (
          <div className="mt-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700 font-medium mb-1">{t("footprints.selectYearToStart")}</p>
                <p className="text-xs text-gray-500">{t("footprints.disclaimer")}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
